import { i18n } from '@lingui/core';
import { detect, fromHtmlTag, fromPath } from '@lingui/detect-locale';
import { cs as dateFnsCsLocale } from 'date-fns/locale/cs';
import { enGB as dateFnsEnLocale } from 'date-fns/locale/en-GB';
import { hr as dateFnsHrLocale } from 'date-fns/locale/hr';
import { sk as dateFnsSkLocale } from 'date-fns/locale/sk';
import { setDefaultOptions } from 'date-fns/setDefaultOptions';

import { isPlaygroundDomain } from '../lib/utils';

export const defaultLocale = 'cs';
export const supportedUILocales = ['en', 'cs', 'hr', 'sk'];

let detectedLocale = defaultLocale;

export function getCurrentLocale() {
  return detectedLocale;
}

/**
 * We detect the locale from the html tag and load the corresponding messages.
 */
export async function dynamicActivate() {
  detectedLocale =
    detect(
      ...[
        isPlaygroundDomain() ? fromPath(0) : undefined,
        fromHtmlTag('lang'),
      ].filter(Boolean),
    ) ?? defaultLocale;

  // If languages are en-US, or cs-CZ, we use only the first part
  const [locale] = detectedLocale.split('-');
  detectedLocale = locale;

  // If the detected locale is not supported, use the default locale
  if (!supportedUILocales.includes(detectedLocale)) {
    console.warn(`Unsupported locale detected: ${detectedLocale}`);
    detectedLocale = defaultLocale;
  }

  try {
    // Try to load the locale
    const { messages } = await import(`./locales/${detectedLocale}.po`);

    // Set date-fns locale
    switch (detectedLocale) {
      case 'cs':
        setDefaultOptions({
          locale: dateFnsCsLocale,
        });
        break;

      case 'hr':
        setDefaultOptions({
          locale: dateFnsHrLocale,
        });
        break;

      case 'sk':
        setDefaultOptions({
          locale: dateFnsSkLocale,
        });
        break;

      default:
        setDefaultOptions({
          locale: dateFnsEnLocale,
        });
        break;
    }

    i18n.load(detectedLocale, messages);
    i18n.activate(detectedLocale);
  } catch (error) {
    console.error(`Failed to load locale: ${detectedLocale}`, error);

    // Fallback to default locale if not already trying to load it
    if (detectedLocale !== defaultLocale) {
      const { messages } = await import(`./locales/${defaultLocale}.po`);

      i18n.load(defaultLocale, messages);
      i18n.activate(defaultLocale);
    }
  }
}
